import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, 
  Users, 
  Shield, 
  Loader2, 
  AlertCircle,
  Check,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchInvite();
  }, [code]);

  const fetchInvite = async () => {
    try {
      const response = await axios.get(`${API}/invites/${code}`);
      setInvite(response.data);
      
      // Pre-compila email se invito nominativo
      if (response.data.email) {
        setFormData(prev => ({ ...prev, email: response.data.email }));
      }
    } catch (error: any) {
      setError(getErrorMessage(error, 'Invito non valido o scaduto'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazioni
    if (!formData.name.trim()) {
      toast.error('Inserisci il tuo nome');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Inserisci la tua email');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/invites/${code}/accept`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        locale: 'it'
      });
      
      // Login automatico
      login(response.data.access_token, response.data.user);
      
      toast.success('Registrazione completata! Benvenuto nel team.');
      navigate('/dashboard');
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella registrazione'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Invito Non Valido</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button variant="outline" onClick={() => navigate('/login')}>
                Vai al Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="h-16 w-16 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Unisciti al Team</CardTitle>
          <CardDescription>
            Sei stato invitato a unirti a un'azienda su OPS!
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Info Azienda */}
          <div className="p-4 rounded-lg bg-muted/50 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{invite.company?.name}</div>
                <div className="text-sm text-muted-foreground capitalize">
                  {invite.company?.type === 'buyer' ? 'Acquirente' : 'Fornitore'}
                </div>
              </div>
            </div>
            
            {invite.role && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Ruolo assegnato</div>
                  <div className="font-medium">{invite.role.name}</div>
                </div>
              </div>
            )}
          </div>
          
          {/* Form Registrazione */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Mario Rossi"
                data-testid="join-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mario@azienda.it"
                disabled={!!invite.email}
                data-testid="join-email-input"
              />
              {invite.email && (
                <p className="text-xs text-muted-foreground">
                  Questo invito è riservato a questa email
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="join-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Conferma Password *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  data-testid="join-confirm-password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={submitting}
              data-testid="join-submit-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Completa Registrazione
            </Button>
          </form>
          
          <div className="text-center text-sm text-muted-foreground">
            Hai già un account?{' '}
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>
              Accedi
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinPage;
